import * as Sentry from '@sentry/browser'
import { isArray, isNumber, isPlainObject, isString, upperFirst } from 'lodash'

// Errors coming from handoffs endpoints which are legacy, but there are some places
// were they are still being used. Those kind of response errors are build by ::ApiExceptions
type HandoffError = { error: string }
type ErrorData = ValidationErrorData | TentacleErrorData | object | string | string[] | undefined

const isHandoffError = (err: object): err is HandoffError =>
  isPlainObject(err) && err.hasOwnProperty('error')
const decorateError = (message: string) => upperFirst(message)
const decorateErrors = (messages: Array<string | number | string[] | object[] | null>) =>
  messages
    .flat()

    .map((err) => {
      if (!err) {
        return ''
      }

      if (isString(err)) {
        return decorateError(err)
      }

      if (isNumber(err)) {
        return decorateError(String(err))
      }

      if (isHandoffError(err)) {
        return err.error
      }

      return ''
    })
    .filter(Boolean)
    .join(', ')

export type ErrorsData = { [key: string]: string | string[] | object[] | null } | string | null

export interface ValidationErrorData {
  errors: ErrorsData
}

export interface TentacleErrorData {
  code: string
  error: string
  message: string
  status_code: number
}

export const isValidationErrorData = (data: ErrorData): data is ValidationErrorData => {
  return data !== undefined && (data as ValidationErrorData).errors !== undefined
}

export const isTentacleError = (data: ErrorData): data is TentacleErrorData => {
  return data !== undefined && (data as TentacleErrorData).status_code !== undefined
}

export const isTentacleErrorType = (
  error: TentacleErrorData | Error
): error is TentacleErrorData => {
  return (error as TentacleErrorData).error !== undefined
}

export const parseErrorData = (data: ErrorData): string => {
  if (isArray(data)) {
    return decorateErrors(data)
  }

  if (isString(data)) {
    return decorateError(data)
  }

  if (isTentacleError(data)) {
    return decorateError(data.message)
  }

  if (isValidationErrorData(data)) {
    // NOTE: review if we want to show these errors as flash errors
    if (isString(data.errors)) {
      return decorateError(data.errors)
    }

    if (isArray(data.errors)) {
      return decorateErrors(data.errors)
    }

    if (data.errors === null) {
      Sentry.captureException(new Error('Unexpected errors response as null from backend'))

      return ''
    }

    return decorateErrors(Object.values(data.errors))
  }

  if (data && isPlainObject(data)) {
    return decorateErrors(Object.values(data))
  }

  Sentry.captureException(new Error('Unexpected parsed error from backend'))

  return ''
}
